import ToastComponent from '@/features/common/components/Toast'
import { useLatestValueRef } from '@/hooks/useLatestValueRef'
import { FC, ReactNode, createContext, use, useCallback, useMemo, useRef, useState } from 'react'
import uuid from 'react-native-uuid'
import invariant from 'tiny-invariant'

interface ContextValue {
    show: (title: string, message: string) => void
}

const Context = createContext<ContextValue | undefined>(undefined)

interface ProviderProps {
    children: ReactNode
}

interface Toast {
    id: string
    title: string
    message: string
}

const Provider: FC<ProviderProps> = ({ children }) => {
    const [currentToast, setCurrentToast] = useState<Toast>()
    const nextToastRef = useRef<Toast>(undefined)

    const currentToastRef = useLatestValueRef(currentToast)

    const hide = useCallback(() => setCurrentToast(undefined), [])
    const show = useCallback(
        (title: string, message: string) => {
            const toast: Toast = { id: uuid.v4(), title, message }

            if (!currentToastRef.current || !nextToastRef.current) return setCurrentToast(toast)

            nextToastRef.current = toast

            hide()
        },
        [currentToastRef, hide]
    )

    const handleExitingAnimationEnd = () => {
        if (!nextToastRef.current) return

        setCurrentToast(nextToastRef.current)
        nextToastRef.current = undefined
    }

    const value = useMemo(() => ({ show }), [show])

    return (
        <Context.Provider value={value}>
            {children}
            {currentToast && (
                <ToastComponent
                    key={currentToast.id}
                    title={currentToast.title}
                    message={currentToast.message}
                    onHide={hide}
                    onExitingAnimationEnd={handleExitingAnimationEnd}
                />
            )}
        </Context.Provider>
    )
}

export default Provider

export const useToast = () => {
    const context = use(Context)

    invariant(context, '"useToast" must be used within a "ToastProvider"')

    return context
}
